import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  Polygon,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import * as turf from "@turf/turf";
import "leaflet/dist/leaflet.css";

// Custom ship icon
const shipIcon = new L.Icon({
  iconUrl: "travel.png",
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Component to capture clicks on map
const MapClickHandler = ({ addPoint }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      addPoint([lat, lng]);
    },
  });
  return null;
};

const ShipMapp = ({ shipPath }) => {
  const [shipData, setShipData] = useState([]);
  const [insidePoints, setInsidePoints] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);

  const [geoFenceCoords, setGeoFenceCoords] = useState([]);

  // ✅ Load geofence from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("geoFenceCoords");
    if (saved) {
      setGeoFenceCoords(JSON.parse(saved));
    }
  }, []);

  // ✅ Save geofence to localStorage whenever it changes
  useEffect(() => {
    if (geoFenceCoords.length > 0) {
      localStorage.setItem("geoFenceCoords", JSON.stringify(geoFenceCoords));
    }
  }, [geoFenceCoords]);

  // Add point with limit (max 5)
  const addGeoFencePoint = (latlng) => {
    setGeoFenceCoords((prev) => {
      if (prev.length >= 5) {
        alert("❌ You can only add up to 5 points for the geofence.");
        return prev;
      }
      return [...prev, latlng];
    });
  };

  // ✅ Clear geofence
  const clearGeoFence = () => {
    setGeoFenceCoords([]);
    localStorage.removeItem("geoFenceCoords");
  };

  // Check if a point is inside the geofence
  const isInsideGeoFence = (point) => {
    if (geoFenceCoords.length < 3) return false;
    const pt = turf.point([point.lng, point.lat]);
    const poly = turf.polygon([
      geoFenceCoords.map(([lat, lng]) => [lng, lat]).concat([[geoFenceCoords[0][1], geoFenceCoords[0][0]]]),
    ]);
    return turf.booleanPointInPolygon(pt, poly);
  };

  // Fetch ship data (or use provided path)
  useEffect(() => {
    if (shipPath) {
      const cleaned = shipPath.filter((point) => point.lat && point.lng);
      const inside = cleaned.filter(isInsideGeoFence);
      setShipData(cleaned);
      setInsidePoints(inside);
    } else {
      fetch("/ship-data.json")
        .then((res) => res.json())
        .then((data) => {
          const cleaned = data.filter((point) => point.lat && point.lng);
          const inside = cleaned.filter(isInsideGeoFence);
          setShipData(cleaned);
          setInsidePoints(inside);
        });
    }
  }, [shipPath, geoFenceCoords]);

  // Animation controls
  const play = () => {
    if (intervalRef.current || insidePoints.length === 0) return;
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= insidePoints.length - 1) {
          pause();
          return prev;
        }
        return prev + 1;
      });
    }, 300);
  };

  const pause = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    if (isPlaying) pause();
    else play();
  };

  const restart = () => {
    pause();
    setCurrentIndex(0);
  };

  const handleSliderChange = (e) => {
    pause();
    setCurrentIndex(parseInt(e.target.value));
  };

  const currentPoint = insidePoints[currentIndex];
  const insidePath = insidePoints.map((p) => [p.lat, p.lng]);

  return (
    <div>
      <MapContainer
        center={[12.97, 77.59]}
        zoom={6}
        style={{ height: "90vh", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
{/* Full path in green */}
{shipData.length > 1 && (
  <Polyline positions={shipData.map((p) => [p.lat, p.lng])} color="green" />
)}

{/* Inside geofence path in blue */}
{insidePath.length > 1 && (
  <Polyline positions={insidePath} color="blue" />
)}

{/* Ship marker (animating if inside geofence) */}
{currentPoint && (
  <Marker position={[currentPoint.lat, currentPoint.lng]} icon={shipIcon}>
    <Popup>
      <strong>Ship Position</strong>
      <br />
      Lat: {currentPoint.lat}
      <br />
      Lng: {currentPoint.lng}
      <br />
      Heading: {currentPoint.heading ?? "N/A"}°
      <br />
      Speed: {currentPoint.speed ?? "N/A"} kn
       <br />
      Time: {currentPoint.time ?? "N/A"}
    </Popup>
  </Marker>
)}

        {/* Click handler for geofence */}
        <MapClickHandler addPoint={addGeoFencePoint} />

        {/* Show geofence polygon */}
        {geoFenceCoords.length > 2 && (
          <Polygon positions={geoFenceCoords} pathOptions={{ color: "red" }} />
        )}

        {/* Inside path */}
        {insidePath.length > 1 && <Polyline positions={insidePath} color="blue" />}

        {/* Animated Ship Marker */}
        {currentPoint && (
          <Marker position={[currentPoint.lat, currentPoint.lng]} icon={shipIcon}>
            <Popup>
              <strong>Ship Position</strong>
              <br />
              Lat: {currentPoint.lat}
              <br />
              Lng: {currentPoint.lng}
              <br />
              Heading: {currentPoint.heading ?? "N/A"}°
              <br />
              Speed: {currentPoint.speed ?? "N/A"} kn
               <br />
      Time: {currentPoint.time ?? "N/A"}
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Controls */}
      <div style={{ textAlign: "center", padding: "1rem" }}>
        <button onClick={togglePlayPause}>
          {isPlaying ? "⏸ Pause" : "▶ Play"}
        </button>
        <button onClick={restart} disabled={currentIndex === 0 && !isPlaying}>
          🔄 Restart
        </button>
        <button onClick={clearGeoFence} style={{ marginLeft: "10px" }}>
          🗑 Clear Geofence
        </button>

        <div style={{ marginTop: "10px", width: "80%", margin: "10px auto" }}>
          <input
            type="range"
            min={0}
            max={insidePoints.length - 1}
            value={currentIndex}
            onChange={handleSliderChange}
            style={{ width: "100%" }}
          />
          <p>
            📍 <strong>Step:</strong> {currentIndex + 1} / {insidePoints.length}
             <br />
  ⏱ <strong>Time:</strong> {currentPoint?.time ?? "N/A"}
          </p>
        </div>
      </div>

      {/* Debug: show coords */}
      {geoFenceCoords.length > 0 && (
        <div style={{ textAlign: "center" }}>
          <h4>Geofence Coords (max 5):</h4>
          <pre>{JSON.stringify(geoFenceCoords, null, 2)}</pre>
        </div>
      )}
      
    </div>
    
  );
};

export default ShipMapp;
